import { maxBy } from 'lodash-es'
import { Canvas, Text } from 'fabric'
import { isWebKit } from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/utils'

/**
 * Масштабирует изображение до заданных размеров с высоким качеством
 * Используется для даунскейлинга hi-res рендера до оригинального размера
 */
async function downscaleImage(
    dataUrl: string,
    targetWidth: number,
    targetHeight: number
): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = targetWidth
            canvas.height = targetHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) {
                reject(new Error('Cannot get 2d context'))
                return
            }

            // Включаем высококачественное сглаживание для лучшего даунскейлинга
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'

            // Рисуем масштабированное изображение
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

            // Для Safari/WebKit используем более агрессивное сжатие для уменьшения размера файла
            // Safari может создавать большие PNG файлы при экспорте canvas, особенно с большими шрифтами
            const isSafari = isWebKit()
            
            // Экспортируем в WebP с высоким качеством
            // WebP даёт лучшее сжатие при сохранении качества текста и поддерживает прозрачность
            let result: string
            try {
                // Для Safari используем более низкое качество WebP для уменьшения размера файла
                // Это помогает избежать ошибки 413 (Payload Too Large) в Safari
                // Для других браузеров используем стандартное качество
                const webpQuality = isSafari ? 0.85 : 0.92
                result = canvas.toDataURL('image/webp', webpQuality)
                
                // Проверяем, что WebP поддерживается
                if (!result.startsWith('data:image/webp')) {
                    // Если WebP не поддерживается, используем PNG
                    // В современных версиях Safari WebP поддерживается, но на случай старых версий
                    result = canvas.toDataURL('image/png')
                }
            } catch {
                // При ошибке используем PNG как fallback
                result = canvas.toDataURL('image/png')
            }

            resolve(result)
        }
        img.onerror = () => reject(new Error('Failed to load image for downscaling'))
        img.src = dataUrl
    })
}
import type {
    TextAlignment,
    FontFamily,
} from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/types'
import {
    convertHexWithAlphaToRgba,
    getNormalizedFontFamily,
} from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/utils'
import { waitForFontLoad } from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/utils/text/textObject'
import {
    TEXT_OBJECT_LINE_HEIGHT,
    TEXT_OBJECT_PADDING,
    TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
} from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/constants'
import { CustomText } from '@/features/analytics/invite-links/ui/Marking/components/MarkingEditor/utils/text/CustomText'

/**
 * Тип для опций конструктора CustomText
 * Включает стандартные свойства Text и кастомные свойства CustomText
 */
type CustomTextOptions = ConstructorParameters<typeof Text>[1] & {
    textBackgroundColor?: string
    textBackgroundPadding?: number
    textBackgroundBorderRadius?: number
}

export type CanvasSize = {
    width: number
    height: number
}

export type LinesContent = {
    lines: string[]
    color: string
    alpha: number
}

export type LinesLayout = {
    widthFraction: number
    minFontSize: number
    paddingBottomHeightFraction: number
}

type FindFontSizeParams = LinesContent & {
    minWidth: number
    paddingBottom: number
} & Omit<LinesLayout, 'widthFraction' | 'paddingBottomHeightFraction'>

const font = (sizePx: number) => `${sizePx}px Ubuntu`

const fillLines = (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    { lines, color, minFontSize, minWidth, paddingBottom }: FindFontSizeParams
) => {
    ctx.save()
    ctx.fillStyle = color
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.globalAlpha = 0.6

    let fontSize = minFontSize
    ctx.font = font(fontSize)

    const longestLine = maxBy(lines, (line) => line.length)
    if (!longestLine) {
        throw new Error(JSON.stringify({ lines }))
    }

    const minFontSizeLongestLine = ctx.measureText(longestLine).width

    if (minFontSizeLongestLine < minWidth) {
        fontSize = Math.floor((minFontSize * minWidth) / minFontSizeLongestLine)
        ctx.font = font(fontSize)
    }


    const lineHeight = fontSize //Math.floor(fontSize * 1.2);
    let lineBottom = canvas.height - paddingBottom
    for (const line of lines.reverse()) {
        ctx.fillText(line, canvas.width / 2, lineBottom)
        lineBottom -= lineHeight
    }
    ctx.restore()
}

export const overlayPng = (
    size: CanvasSize,
    {
        lines,
        color,
        alpha,
        minFontSize,
        widthFraction,
        paddingBottomHeightFraction,
    }: LinesContent & LinesLayout
) => {
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height

    const ctx = canvas.getContext('2d')
    if (!ctx) {
        throw new Error(`canvas.getContext('2d') returns null`)
    }

    fillLines(canvas, ctx, {
        lines,
        color,
        alpha,
        minFontSize,
        minWidth: canvas.width * widthFraction,
        paddingBottom: canvas.height * paddingBottomHeightFraction,
    })

    return canvas.toDataURL('image/png')
}

/**
 * Информация о масштабе для пересчета позиций
 */
export interface MarkingEditorScale {
    canvasWidth: number
    canvasHeight: number
    originalWidth: number
    originalHeight: number
}

/**
 * Параметры для экспорта overlay маркировки из редактора
 */
export interface ExportMarkingOverlayParams {
    /**
     * Размеры медиа (для создания overlay того же размера)
     */
    imageWidth: number
    imageHeight: number

    /**
     * Текст маркировки
     */
    text: string

    /**
     * Размер шрифта (4-18)
     */
    fontSize: number

    /**
     * Цвет текста (hex с alpha)
     */
    textColor: string

    /**
     * Выравнивание текста
     */
    textAlignment: TextAlignment

    /**
     * Шрифт текста
     */
    fontFamily: FontFamily

    /**
     * Включена ли подложка
     */
    hasBackground: boolean

    /**
     * Цвет подложки (hex с alpha, null если подложка выключена)
     */
    backgroundColor: string | null

    /**
     * Позиция X в процентах (0-100)
     */
    positionX: number

    /**
     * Позиция Y в процентах (0-100)
     */
    positionY: number

    /**
     * Информация о масштабе для пересчета позиций (опционально)
     * Если не указана, позиции считаются относительно оригинального изображения
     */
    scale?: MarkingEditorScale | null
}

/**
 * Экспорт overlay маркировки с использованием Fabric.js
 * Создает overlay только с текстом (и подложкой, если включена)
 * Размеры overlay соответствуют размерам оригинального медиа
 *
 * @param params - параметры для создания overlay
 * @returns Promise с data URL overlay изображения
 */
export async function exportMarkingOverlay(
    params: ExportMarkingOverlayParams
): Promise<string> {
    const {
        imageWidth,
        imageHeight,
        text,
        fontSize,
        textColor,
        textAlignment,
        fontFamily,
        hasBackground,
        backgroundColor,
        positionX,
        positionY,
        scale,
    } = params

    // Создаем canvas с размерами медиа
    const canvasElement = document.createElement('canvas')
    canvasElement.width = imageWidth
    canvasElement.height = imageHeight

    // Создаем Fabric.js canvas
    const fabricCanvas = new Canvas(canvasElement, {
        backgroundColor: 'transparent',
        renderOnAddRemove: false,
    })

    // Конвертируем цвет текста из hex с alpha в rgba
    const textColorRgba = convertHexWithAlphaToRgba(textColor)

    // ВАЖНО: Проверяем, что fontSize передан и в правильном диапазоне
    if (fontSize < 4 || fontSize > 18) {
    }

    // Пересчитываем размер шрифта и позиции с учетом масштаба, если он указан
    let fontSizeOriginal = fontSize
    let positionXOriginal = positionX
    let positionYOriginal = positionY

    if (scale) {
        // Рассчитываем масштаб изображения на canvas
        // actualScale показывает, во сколько раз изображение уменьшено на canvas относительно оригинала
        const scaleX = scale.canvasWidth / scale.originalWidth
        const scaleY = scale.canvasHeight / scale.originalHeight
        const actualScale = Math.min(scaleX, scaleY) // Используем тот же масштаб, что и в редакторе

        // ВАЖНО: Размер шрифта в редакторе (fontSize) - это размер в пикселях на canvas
        // Canvas меньше оригинала, поэтому текст визуально меньше
        // Для оригинала нужно увеличить размер шрифта обратно пропорционально масштабу
        // Например: если canvas в 2 раза меньше оригинала (actualScale = 0.5),
        // то шрифт 12px на canvas должен быть 24px на оригинале: 12 / 0.5 = 24
        //
        // Если actualScale > 1 (canvas больше оригинала - редко, но возможно),
        // то нужно уменьшить размер: 12 / 2 = 6
        fontSizeOriginal = fontSize / actualScale


        // Позиция текста на canvas в пикселях (позиция центра текста)
        // positionX и positionY - это позиция центра текста в процентах (0-100) относительно canvas
        const pixelXOnCanvas = (positionX / 100) * scale.canvasWidth
        const pixelYOnCanvas = (positionY / 100) * scale.canvasHeight

        // Изображение центрировано на canvas, поэтому нужно учесть смещение
        const imageScaledWidth = scale.originalWidth * actualScale
        const imageScaledHeight = scale.originalHeight * actualScale
        const offsetX = (scale.canvasWidth - imageScaledWidth) / 2
        const offsetY = (scale.canvasHeight - imageScaledHeight) / 2

        // Позиция относительно масштабированного изображения (учитываем смещение)
        // НЕ ограничиваем позицию границами изображения - текст может выходить за границы
        const pixelXOnScaledImage = pixelXOnCanvas - offsetX
        const pixelYOnScaledImage = pixelYOnCanvas - offsetY

        // Позиция на оригинале в пикселях (переводим из масштабированных координат в оригинальные)
        const pixelXOnOriginal = pixelXOnScaledImage / actualScale
        const pixelYOnOriginal = pixelYOnScaledImage / actualScale

        // Позиция на оригинале в процентах
        // Это позиция центра текста на оригинале
        positionXOriginal = (pixelXOnOriginal / imageWidth) * 100
        positionYOriginal = (pixelYOnOriginal / imageHeight) * 100
    }

    // Вычисляем позицию текста в пикселях
    // Позиция в процентах (0-100) означает позицию центра текста
    // Сначала создаем временный текстовый объект для получения размеров
    // ВАЖНО: Используем fontSizeOriginal БЕЗ clampFontSize для временного объекта,
    // чтобы получить правильные размеры текста, но затем применим clampFontSize к финальному объекту
    // Также создаем временный объект с подложкой, если она включена, чтобы получить правильные размеры
    // Нормализуем имя шрифта для правильной загрузки
    const normalizedFontFamily = getNormalizedFontFamily(fontFamily)

    // ВАЖНО: Ждем загрузки шрифта перед созданием текстового объекта
    // Это критично для Inter/Montserrat и других шрифтов, которые загружаются асинхронно
    await waitForFontLoad(fontFamily)

    // Определяем, является ли шрифт проблемным (Inter, Montserrat)
    const isKerningSensitiveFont =
        fontFamily === 'Inter' || fontFamily === 'Montserrat'

    // ВАЖНО: Масштабируем padding и borderRadius пропорционально изменению fontSize
    // В редакторе fontSize ~12px с padding 2px и borderRadius 2px
    // При экспорте fontSize может быть больше (например, 24px, 48px)
    // Чтобы подложка выглядела так же, как в редакторе, масштабируем padding и borderRadius
    const fontSizeScale = fontSizeOriginal / fontSize // Коэффициент масштабирования
    const scaledPadding = Math.round(TEXT_OBJECT_PADDING.BACKGROUND * fontSizeScale)
    const scaledBorderRadius = Math.round(TEXT_OBJECT_BACKGROUND_BORDER_RADIUS * fontSizeScale)

    // Используем CustomText для временного объекта, чтобы получить правильные размеры с учетом кастомной подложки
    const tempTextObj = new CustomText(
        text,
        {
            fontSize: fontSizeOriginal, // Используем оригинальный размер без clamp для точного расчета
            fill: textColorRgba,
            textAlign: textAlignment as 'left' | 'center' | 'right',
            fontFamily: normalizedFontFamily,
            left: 0,
            top: 0,
            originX: textAlignment as 'left' | 'center' | 'right',
            // Уменьшаем межстрочное расстояние для слипания подложек (как в редакторе)
            lineHeight: TEXT_OBJECT_LINE_HEIGHT,
            // Применяем charSpacing для проблемных шрифтов (Inter, Montserrat)
            charSpacing: isKerningSensitiveFont ? -15 : 0,
            // Применяем подложку, если она задана (с масштабированными значениями)
            textBackgroundColor: hasBackground && backgroundColor
                ? convertHexWithAlphaToRgba(backgroundColor)
                : undefined,
            textBackgroundPadding: hasBackground && backgroundColor
                ? scaledPadding
                : undefined,
            textBackgroundBorderRadius: hasBackground && backgroundColor
                ? scaledBorderRadius
                : undefined,
        } as CustomTextOptions
    )

    // Для проблемных шрифтов нужно пересчитать размеры после применения charSpacing
    if (isKerningSensitiveFont) {
        tempTextObj.initDimensions()
        // Применяем подложку после initDimensions() (с масштабированными значениями)
        if (hasBackground && backgroundColor) {
            tempTextObj.textBackgroundColor = convertHexWithAlphaToRgba(backgroundColor)
            tempTextObj.textBackgroundPadding = scaledPadding
            tempTextObj.textBackgroundBorderRadius = scaledBorderRadius
        }
    }

    // Получаем размеры текста (с учетом подложки, если она есть)
    // ВАЖНО: В Fabric.js textObj.width включает padding, если есть textBackgroundPadding
    // Это правильно для расчета позиции, так как мы позиционируем весь блок (текст + подложка)
    const textWidth = tempTextObj.width * (tempTextObj.scaleX ?? 1)
    const textHeight = tempTextObj.height * (tempTextObj.scaleY ?? 1)

    // Конвертируем проценты в пиксели
    // positionXOriginal и positionYOriginal - это позиция центра текста (включая подложку) относительно оригинала
    const centerX = (positionXOriginal / 100) * imageWidth
    const centerY = (positionYOriginal / 100) * imageHeight

    // Корректируем позицию в зависимости от выравнивания
    // В Fabric.js при originX и textAlign:
    // - 'center' -> left это центр текста (включая подложку)
    // - 'left' -> left это левый край текста (включая подложку)
    // - 'right' -> left это правый край текста (включая подложку)
    // positionXOriginal - это позиция центра текста (включая подложку)
    let textLeft = centerX
    if (textAlignment === 'left') {
        // Для left нужно сдвинуть влево на половину ширины (от центра к левому краю)
        // В Fabric.js при textAlign: 'left' и originX: 'left', left - это левый край
        textLeft = centerX - textWidth / 2
    } else if (textAlignment === 'right') {
        // Для right нужно сдвинуть вправо на половину ширины (от центра к правому краю)
        // В Fabric.js при textAlign: 'right' и originX: 'right', left - это правый край
        textLeft = centerX + textWidth / 2
    }
    // Для center left уже равен centerX (центр текста = центр позиции)

    // Для вертикального центрирования: top - это верхний край, поэтому вычитаем половину высоты
    const textTop = centerY - textHeight / 2


    // НЕ ограничиваем позицию текста границами изображения
    // Текст может выходить за границы, как и в редакторе
    // При экспорте overlay накладывается на изображение, и части за границами просто обрезаются
    const finalTextTop = textTop

    // Создаем финальный текстовый объект с правильной позицией и размером шрифта
    // ВАЖНО: НЕ применяем clampFontSize для экспорта, чтобы сохранить точный размер
    // clampFontSize ограничивает до 4-18, но после масштабирования размер может быть больше 18
    // Для экспорта используем оригинальный размер без ограничений
    const finalFontSize = Math.max(1, Math.round(fontSizeOriginal)) // Минимум 1px, округляем


    // Создаем текстовый объект через CustomText для поддержки кастомной подложки с padding и border radius
    const textObj = new CustomText(
        text,
        {
            fontSize: finalFontSize,
            fill: textColorRgba,
            textAlign: textAlignment as 'left' | 'center' | 'right',
            fontFamily: normalizedFontFamily,
            left: textLeft,
            top: finalTextTop,
            originX: textAlignment as 'left' | 'center' | 'right',
            // Уменьшаем межстрочное расстояние для слипания подложек (как в редакторе)
            lineHeight: TEXT_OBJECT_LINE_HEIGHT,
            // Применяем charSpacing для проблемных шрифтов (Inter, Montserrat)
            charSpacing: isKerningSensitiveFont ? -15 : 0,
            selectable: false,
            evented: false,
            // Применяем подложку, если она задана (с масштабированными значениями)
            textBackgroundColor: hasBackground && backgroundColor
                ? convertHexWithAlphaToRgba(backgroundColor)
                : undefined,
            textBackgroundPadding: hasBackground && backgroundColor
                ? scaledPadding
                : undefined,
            textBackgroundBorderRadius: hasBackground && backgroundColor
                ? scaledBorderRadius
                : undefined,
        } as CustomTextOptions
    )

    // Убираем обводку для экспорта (она нужна только в редакторе)
    textObj.set({
        hasBorders: false,
        borderColor: 'transparent',
    })

    // Добавляем текстовый объект (подложка уже включена через textBackgroundColor)
    fabricCanvas.add(textObj)

    // Для проблемных шрифтов делаем пересчёт размеров через requestAnimationFrame
    // Это необходимо для корректного отображения текста с charSpacing
    if (isKerningSensitiveFont) {
        // Используем Promise для ожидания следующего кадра перед рендером
        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
                // Принудительно применяем charSpacing (на случай сброса)
                textObj.set({ charSpacing: -15 })
                textObj.initDimensions()

                // Применяем подложку после initDimensions() (с масштабированными значениями)
                if (hasBackground && backgroundColor) {
                    textObj.textBackgroundColor = convertHexWithAlphaToRgba(backgroundColor)
                    textObj.textBackgroundPadding = scaledPadding
                    textObj.textBackgroundBorderRadius = scaledBorderRadius
                }

                textObj.setCoords()
                fabricCanvas.renderAll()
                resolve()
            })
        })
    } else {
        // Для обычных шрифтов сразу рендерим
        fabricCanvas.renderAll()
    }

    // Оптимизируем качество текста: рендерим в 2x разрешении для чёткости,
    // затем масштабируем обратно до оригинального размера для совместимости с сервером
    // Это особенно важно для маленьких шрифтов, которые иначе размываются
    // Для Safari и больших размеров шрифта (от 7 и выше) используем меньший множитель
    // для уменьшения размера файла и избежания ошибки 413 (Payload Too Large)
    const isSafari = isWebKit()
    // Используем оригинальный fontSize из параметров (в диапазоне 4-18), а не fontSizeOriginal
    // который может быть больше после масштабирования
    const isLargeFontSize = fontSize >= 7
    // Для Safari с большими шрифтами используем 1.5x вместо 2x для уменьшения размера файла
    // Для остальных случаев используем 2x для максимального качества
    const RENDER_MULTIPLIER = isSafari && isLargeFontSize ? 1.5 : 2

    // Рендерим в высоком разрешении
    const hiResDataUrl = fabricCanvas.toDataURL({
        multiplier: RENDER_MULTIPLIER,
        format: 'png' as const, // PNG для промежуточного этапа без потерь
    })

    // Масштабируем обратно до оригинального размера
    const dataUrl = await downscaleImage(hiResDataUrl, imageWidth, imageHeight)

    // Очищаем canvas
    fabricCanvas.dispose()

    return dataUrl
}
